import { ActionContext, MutationTree, ActionTree, GetterTree } from 'vuex'
import { AdhocDriveListItem, AdhocDrive, Customer, Country, AdhocDriveFilter, Provider, Currency, Service_Leg } from '../webApi/DataModels'
import webApi from '../webApi'
import { each, extend, find, orderBy } from 'lodash'
import moment from 'moment'

interface IAdhocDriveListFilter {
  Customer_IDs: number[];
  Country_IDs: number[];
  Provider_IDs: number[];
  From_Date: string;
  To_Date: string;
}

function resolveValues (id: number, values: any[], idFieldName: string, valueFieldName: string, resolvedLabel: string): String {
  var resolvedValues = []
  var filterObject = {}
  filterObject[idFieldName] = id
  var value = find(values, filterObject)
  if (value && value[valueFieldName]) {
    resolvedValues.push(value[valueFieldName])
  }
  if (resolvedValues.length > 3) {
    return resolvedValues.length + ' ' + resolvedLabel
  } else if (resolvedValues.length === 0) {
    return ''
  } else {
    return resolvedValues.join(', ')
  }
}

class AdhocDriveState {
  public adhocDriveList: AdhocDriveListItem[] = []
  public filteredAdhocDriveList: AdhocDriveListItem[] = []
  public loadedAdhocDrive: AdhocDrive = null
  public adhocDriveResult: any = null
  public currentEditAdhocDrive: AdhocDrive = null
  public adhocDriveFilter: AdhocDriveFilter = null
}

const mutations = <MutationTree<AdhocDriveState>> {
  // sets the adhocDrive list and resolves the country, customer and service level names
  setAdhocDriveList (state: AdhocDriveState, { list, customers, countries, providers, currencies, serviceLegs }: { list: AdhocDriveListItem[], customers: Customer[], countries: Country[], providers: Provider[], currencies: Currency[], serviceLegs: Service_Leg[] }) {
    each(list, (adhocDriveItem: AdhocDrive, index: number) => {
      extend(adhocDriveItem, {
        Country_Name: resolveValues(adhocDriveItem.Country_ID, countries, 'Country_ID', 'Country_Name', 'Countries')
      })

      extend(adhocDriveItem, {
        Customer_Name: resolveValues(adhocDriveItem.Customer_ID, customers, 'Customer_ID', 'Customer_Name', 'Customers')
      })

      extend(adhocDriveItem, {
        Provider_Name: resolveValues(adhocDriveItem.Provider_ID, providers, 'Provider_ID', 'Provider_Name', 'Providers')
      })

      extend(adhocDriveItem, {
        Currency_Name: resolveValues(adhocDriveItem.Currency_ID, currencies, 'Currency_ID', 'Currency_Code', 'Currencies')
      })

      extend(adhocDriveItem, {
        Service_Leg_Name: resolveValues(adhocDriveItem.Service_Leg_ID, serviceLegs, 'Service_Leg_ID', 'Service_Leg_Name', 'service_Legs')
      })

      extend(adhocDriveItem, {
        User_Name: adhocDriveItem.User.User_Name
      })
    })

    state.adhocDriveList = list
    state.filteredAdhocDriveList = list
  },
  setCurrentEditAdhocDrive (state: AdhocDriveState, adhocDrive: AdhocDrive): void {
    state.currentEditAdhocDrive = adhocDrive
  },
  setLoadedAdhocDrive (state: AdhocDriveState, adhocDrive: AdhocDrive): void {
    state.loadedAdhocDrive = adhocDrive
  },
  filteredAdhocDriveList (state:AdhocDriveState, { sortFields, sortDirections }) {
    state.filteredAdhocDriveList = orderBy(state.filteredAdhocDriveList, sortFields, sortDirections)
    state.adhocDriveList = orderBy(state.adhocDriveList, sortFields, sortDirections)
  },
  setAdhocDriveFilter (state: AdhocDriveState, adhocDriveFilter: AdhocDriveFilter) {
    state.adhocDriveFilter = adhocDriveFilter
  },
  reset (state:AdhocDriveState) {
    state.adhocDriveList = []
    state.filteredAdhocDriveList = []
    state.loadedAdhocDrive = null
    state.adhocDriveResult = null
    state.currentEditAdhocDrive = null
    state.adhocDriveFilter = null
  },
  resetAdhocDrive (state: AdhocDriveState): void {
    state.loadedAdhocDrive = null
  }
}

const actions = <ActionTree<AdhocDriveState, any>>{
  reset (store: ActionContext<AdhocDriveState, any>):void {
    store.commit('reset')
  },
  loadAdhocDriveFilter (store: ActionContext<AdhocDriveState, any>, adhocDriveFilter: AdhocDriveFilter) {
    store.commit('setAdhocDriveFilter', adhocDriveFilter)
  },
  // loads the adhocDrive list
  loadAdhocDriveList (store: ActionContext<AdhocDriveState, any>, adhocDriveListFilter: IAdhocDriveListFilter): Promise<any> {
    return webApi.adhocDrive.get_AdhocDrive_List(adhocDriveListFilter).then(
      (response) => {
        store.commit('setAdhocDriveList', {
          list: response.data,
          customers: store.rootState.customer.customers,
          countries: store.rootState.country.countries,
          providers: store.rootState.provider.activeProviders,
          currencies: store.rootState.currency.currencies,
          serviceLegs: store.rootState.serviceLeg.service_Legs
        })
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // loads a adhocDrive
  loadAdhocDrive (store: ActionContext<AdhocDriveState, any>, adhocDriveID: number): Promise<any> {
    store.commit('resetAdhocDrive')
    return webApi.adhocDrive.get_AdhocDrive_By_ID(adhocDriveID).then(
      (response: any) => {
        var result: AdhocDrive = response.data as AdhocDrive
        store.commit('setLoadedAdhocDrive', result)
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  updateAdhocDrive (store: ActionContext<AdhocDriveState, any>, adhocDrive: any): Promise<any> {
    return webApi.adhocDrive.update_AdhocDrive(adhocDrive).then(
      (response) => {
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // saves the adhocDrive to the database
  saveAdhocDrive (store: ActionContext<AdhocDriveState, any>, adhocDrive: AdhocDrive): Promise<any> {
    return webApi.adhocDrive.save_AdhocDrive(adhocDrive).then(
      (response) => {
        var filter = {
          customers: [adhocDrive.Customer_ID],
          countries: [adhocDrive.Country_ID],
          providers: [],
          fromDate: moment(adhocDrive.Date_Of_Order).format(),
          toDate: moment(adhocDrive.Date_Of_Order).format()
        }
        store.commit('setAdhocDriveFilter', filter)
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  sortList ({ commit }, { sortFields, sortDirections }): void {
    commit('filteredAdhocDriveList', { sortFields, sortDirections })
  }
}

const getters = <GetterTree<AdhocDriveState, any>>{
  adhocDriveList: (state): AdhocDriveListItem[] => {
    return state.filteredAdhocDriveList
  },
  loadedAdhocDrive: (state): AdhocDrive => {
    return state.loadedAdhocDrive
  },
  adhocDriveResult: (state): any => {
    return state.adhocDriveResult
  },
  adhocDriveFilter: (state: AdhocDriveState): AdhocDriveFilter => {
    return state.adhocDriveFilter
  }
}

export default ({
  namespaced: true,
  state: new AdhocDriveState(),
  mutations,
  actions,
  getters
})
